import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

/**
 * O `refreshToken` NÃO vive mais aqui (at-22). Ele é entregue pelo backend em
 * cookie httpOnly e o `/auth/refresh` o envia via `withCredentials` — guardar
 * uma cópia no Redux (persistido em localStorage) era manter um segredo de 7
 * dias legível por qualquer script da página, sem nenhum leitor no código.
 */
interface AuthState {
  email: string;
  accessToken: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  /**
   * `true` quando a última tentativa de renovação falhou por INDISPONIBILIDADE
   * (rede/timeout/5xx) — e não por sessão expirada. Existe no store porque quem
   * decide o logout (`PrivateRoute`) não é quem faz a renovação: sem esse sinal
   * compartilhado, o `PrivateRoute` veria "token expirado + ninguém renovando"
   * ao fim da tentativa e deslogaria justamente o usuário offline que a at-22
   * existe para preservar.
   */
  sessaoIndisponivel: boolean;
  isAdmin: boolean;
  userType: string | null;
  userId: string | null;
  codigoAssociado: string | null;
  codigoRegional: string | null;
  codigoCooperativa: string | null;
}

const initialState: AuthState = {
  email: "",
  accessToken: "",
  isAuthenticated: false,
  isLoading: false,
  isRefreshing: false,
  sessaoIndisponivel: false,
  isAdmin: false,
  userType: null,
  userId: null,
  codigoAssociado: null,
  codigoRegional: null,
  codigoCooperativa: null,
};

const applyToken = (state: AuthState, token: string) => {
  const decoded: any = jwtDecode(token);
  state.userId = decoded.sub || null;
  state.isAdmin = !!decoded.isAdmin;
  state.userType = decoded.userType || decoded.user_type || null;
  state.codigoAssociado = decoded.codigoAssociado || null;
  state.codigoRegional = decoded.codigo_regional || null;
  state.codigoCooperativa = decoded.codigo_cooperativa || null;
};

/**
 * Remove o `refreshToken` que versões anteriores gravavam no slice. O
 * redux-persist reidrata o objeto persistido inteiro, então a chave legada
 * sobrevive em `persist:root` até ser explicitamente apagada — é o que este
 * helper faz em toda transição de sessão (login, falha de login, logout).
 */
const removerRefreshTokenLegado = (state: AuthState) => {
  delete (state as AuthState & { refreshToken?: string }).refreshToken;
};

const clearUser = (state: AuthState) => {
  state.userId = null;
  state.isAdmin = false;
  state.userType = null;
  state.codigoAssociado = null;
  state.codigoRegional = null;
  state.codigoCooperativa = null;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginRequest(state) {
      state.isLoading = true;
    },
    loginSuccess(
      state,
      action: PayloadAction<{ email: string; accessToken: string }>
    ) {
      state.email = action.payload.email;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      removerRefreshTokenLegado(state);
      try {
        applyToken(state, action.payload.accessToken);
      } catch {
        clearUser(state);
      }
    },
    loginFailure(state) {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.accessToken = "";
      state.email = "";
      removerRefreshTokenLegado(state);
      clearUser(state);
    },
    logout(state) {
      state.email = "";
      state.accessToken = "";
      state.isAuthenticated = false;
      state.isRefreshing = false;
      state.sessaoIndisponivel = false;
      removerRefreshTokenLegado(state);
      clearUser(state);
    },
    startRefreshingToken(state) {
      state.isRefreshing = true;
      // Toda nova tentativa zera o diagnóstico anterior: se o flag persistido
      // ficou `true` de uma sessão offline, ele se resolve na primeira tentativa.
      state.sessaoIndisponivel = false;
    },
    /**
     * Encerra o estado de "renovando" SEM tocar no token (at-22). Usada quando o
     * refresh falha por indisponibilidade: a sessão continua de pé, apenas a
     * tentativa terminou. O caminho de sucesso usa `setAccessToken` + esta action
     * (a antiga `finishRefreshingToken` fazia as duas coisas e, chamada com
     * string vazia, apagava o token de uma sessão válida).
     */
    stopRefreshingToken(state, action: PayloadAction<{ indisponivel: boolean }>) {
      state.isRefreshing = false;
      state.sessaoIndisponivel = action.payload.indisponivel;
    },
    setAccessToken(state, action: PayloadAction<string>) {
      state.accessToken = action.payload;
      state.isAuthenticated = true;
      try {
        applyToken(state, action.payload);
      } catch {
        clearUser(state);
      }
    },
    clearAccessToken(state) {
      state.accessToken = "";
      state.isAuthenticated = false;
      clearUser(state);
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  startRefreshingToken,
  stopRefreshingToken,
  setAccessToken,
  clearAccessToken,
} = authSlice.actions;

export default authSlice.reducer;
